import type { Query } from "@tanstack/react-query"
import type { OpenapiQueryClient } from "openapi-react-query"

import type { SafeAny } from "@/types/utils"

type ExtractEndpoint<T> = T extends OpenapiQueryClient<infer E, SafeAny> ? keyof E & string : string

type FindQueriesByEndpointTarget<T> = {
  endpoint: ExtractEndpoint<T>
  options?: {
    paramsFilter?: Array<string | undefined>
    paramsFilterMatch?: "loose" | "exact"
    endpointMatch?: "exact" | "contains"
  }
}[]

/* Filters queries by endpoint substring and optional params values. */
export function findQueriesByEndpoint<T extends OpenapiQueryClient<SafeAny, SafeAny>>(
  /* The array of queries to filter */
  queries: Query[],
  /* The endpoint(s) to match against, with optional filtering options */
  targetEndpoint: FindQueriesByEndpointTarget<T>
): Query[] {
  return queries.filter(q => {
    const queryKey = q.queryKey

    // Query keys from openapi-fetch/openapi-react-query follow the structure:
    // [method, endpoint, { params: { header, path, query, ... } }]
    if (!Array.isArray(queryKey) || queryKey.length < 2) {
      return false
    }

    const endpoint = queryKey[1]

    // Check if endpoint matches any of the target endpoints
    if (typeof endpoint !== "string") {
      return false
    }

    // Find matching target endpoint configuration
    const matchingTarget = targetEndpoint.find(te => {
      const endpointMatch = te.options?.endpointMatch ?? "contains"

      if (endpointMatch === "exact") {
        return endpoint === te.endpoint
      } else {
        return endpoint.includes(te.endpoint)
      }
    })

    if (!matchingTarget) {
      return false
    }

    const { options } = matchingTarget

    // If no params filter provided, we're done
    if (!options?.paramsFilter || options.paramsFilter.length === 0) {
      return true
    }

    const { paramsFilter, paramsFilterMatch = "loose" } = options

    // Search through the queryKey array for any objects that contain the filter values
    const hasMatchingValue = (obj: unknown, filterValue: string): boolean => {
      if (typeof obj === "string") {
        return obj === filterValue
      }

      if (typeof obj === "object" && obj !== null) {
        // Recursively check all values in the object
        return Object.values(obj).some(value => hasMatchingValue(value, filterValue))
      }

      return false
    }

    // Check if a filter value exists anywhere in the queryKey
    const containsFilterValue = (filterValue: string): boolean =>
      queryKey.some(item => hasMatchingValue(item, filterValue))

    // Filter out falsy values once
    const validFilters = paramsFilter.filter(Boolean)

    // If all filters were falsy, treat as no filter (no constraints)
    if (validFilters.length === 0) {
      return true
    }

    // Apply match logic
    if (paramsFilterMatch === "exact") {
      // ALL filter values must be present
      return validFilters.every(filterValue => containsFilterValue(filterValue))
    } else {
      // ANY filter value match is sufficient (loose)
      return validFilters.some(filterValue => containsFilterValue(filterValue))
    }
  })
}
